import type { ReactNode } from 'react';

/** A white card with a heading — the one container every tab on this page uses. */
export function Card({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-solid border-pearl bg-white p-5">
      <div className="mb-3 flex items-start justify-between gap-4">
        <h2 className="text-base font-semibold text-zinc-900">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Where a part of the project stands. Plain words, never a colour alone. */
export function Status({ tone, children }: { tone: 'live' | 'preview' | 'next' | 'later'; children: ReactNode }) {
  const cls = {
    live: 'bg-green-50 text-green-800 border-green-200',
    preview: 'bg-amber-50 text-amber-900 border-amber-200',
    next: 'bg-blue-50 text-blue-800 border-blue-200',
    later: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  }[tone];
  return <span className={`whitespace-nowrap rounded border border-solid px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

/** An empty table or list that says what will appear there, and when. */
export function EmptyState({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-zinc-300 p-8 text-center">
      <p className="text-sm font-medium text-zinc-700">{title}</p>
      <div className="mx-auto mt-1 max-w-xl text-sm text-zinc-500">{children}</div>
    </div>
  );
}

export type Group = 'vetted' | 'leads' | 'internal';

const GROUP_LABEL: Record<Group, string> = { vetted: 'Vetted Talent', leads: 'Leads', internal: 'Internal Talents' };

/**
 * The group label every candidate row carries (design v0.6 §5): solid black = Vetted Talent,
 * blue = Leads, dashed outline = Internal. The words are always there, never the colour alone.
 */
export function GroupTag({ group }: { group: Group }) {
  const cls = {
    vetted: 'border-solid bg-zinc-900 text-white border-zinc-900',
    leads: 'border-solid bg-blue-600 text-white border-blue-600',
    internal: 'bg-white text-zinc-700 border-dashed border-zinc-500',
  }[group];
  return <span className={`whitespace-nowrap rounded border px-2 py-0.5 text-xs font-medium ${cls}`}>{GROUP_LABEL[group]}</span>;
}

/** A number that has no data behind it yet. */
export function Kpi({ label, sub, tag }: { label: string; sub: string; tag?: ReactNode }) {
  return (
    <section className="rounded-lg border border-solid border-pearl bg-white p-4">
      <p className="text-xs text-zinc-500">{label}</p>
      {tag && <div className="mt-1">{tag}</div>}
      <p className="mt-1 text-2xl font-semibold text-zinc-400">—</p>
      <p className="mt-1 text-xs text-zinc-500">{sub}</p>
    </section>
  );
}
